// SIL-1364. Spec: docs/superpowers/specs/2026-09-29-exchange-purchase-terms-design.md
import { z } from 'zod';

/**
 * SIL-1364. Versions the purchase-terms TEMPLATE, not terms.
 *
 * Deliberately unlike EXCHANGE_UA_VERSION: bumping this invalidates no past
 * consent and gates nobody. A consent records what someone saw on a day that
 * has already happened, so it cannot go stale. Do not add a re-acceptance
 * path here — see the spec, section 5.
 */
export const PURCHASE_TERMS_VERSION = 'PT-2026-09-29';

export const bindingEventSchema = z.enum([
  'offer_deposit',
  'auction_bid',
  'card_checkout',
]);
export type BindingEvent = z.infer<typeof bindingEventSchema>;

/** Fields every facts snapshot carries, whatever the binding event. */
const commonFacts = {
  priceUsd: z.string(),
  quantity: z.number().int().positive(),
  hardwareDescription: z.string(),
  /** Forward sale settlement date; null for a spot listing (listing.available_on). */
  availableOn: z.string().nullable().default(null),
};

/** Business path. Escrow, split into a deposit and a balance. */
export const offerDepositFactsSchema = z
  .object({
    ...commonFacts,
    depositUsd: z.string(),
    balanceUsd: z.string(),
    escrowAgent: z.literal('Escrow.com'),
    escrowFeeSplit: z.literal('50/50'),
  })
  .strict();

/** Either org type. The price shown is that bid. */
export const auctionBidFactsSchema = z
  .object({
    ...commonFacts,
    bidUsd: z.string(),
    auctionClosesAt: z.string(),
  })
  .strict();

/**
 * Individual path only. NO deposit or balance keys — an individual checkout
 * is paid in full, so .strict() rejects them rather than silently ignoring.
 */
export const cardCheckoutFactsSchema = z.object({ ...commonFacts }).strict();

/**
 * A positive USD amount, at most 2 decimal places, matching the
 * `bid_amount_usd numeric(12,2)` column it is ultimately compared against
 * (965_purchase_terms_consent.sql / 765_bid.sql). Deliberately a string, not
 * a number, like the rest of this DTO's money fields — money is never a JS
 * float here.
 *
 * Rejects the three reachable-but-wrong shapes a bare `z.string()` let
 * through: a non-numeric string (would 500 inside place_bid's `numeric`
 * cast instead of failing validation), a negative amount (would write
 * permanent garbage into this append-only table), and more than 2 decimal
 * places (the numeric(12,2) column rounds while `facts` keeps the raw
 * string, so the frozen evidence and the column place_bid matches on would
 * disagree, and the bid would then fail to find its consent).
 */
export const moneyAmountUsdSchema = z
  .string()
  .regex(/^\d+(\.\d{1,2})?$/, 'must be a positive amount with at most 2 decimal places')
  .refine((v) => Number(v) > 0, 'must be greater than 0');

/**
 * SIL-1364. `db/schema/965_purchase_terms_consent.sql`'s reference CHECK,
 * mirrored: exactly one reference and it must match `bindingEvent`.
 *
 * `auction_bid` references the listing and the bid amount, never a bid id —
 * a consent is taken BEFORE `place_bid` creates the bid row, so it cannot
 * reference one that does not exist yet, and this table is append-only (no
 * UPDATE to stamp one on afterwards). See the migration's own comment.
 */
export const purchaseTermsConsentCreateSchema = z
  .object({
    bindingEvent: bindingEventSchema,
    offerId: z.string().uuid().optional(),
    listingId: z.string().uuid().optional(),
    bidAmountUsd: moneyAmountUsdSchema.optional(),
  })
  .strict();
export type PurchaseTermsConsentCreate = z.infer<
  typeof purchaseTermsConsentCreateSchema
>;
